import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { fetchTools } from "../lib/api";
import { CATEGORY_COLORS, getCategoryStyle } from "../lib/categoryStyle";

export default function RelatedTools({ currentSlug, category }) {
    const [related, setRelated] = useState([]);
    const [popular, setPopular] = useState([]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                // Same-category tools (exclude current)
                const sameCat = await fetchTools({ category });
                const filtered = sameCat
                    .filter((t) => t.slug !== currentSlug)
                    .slice(0, 6);
                if (!cancelled) setRelated(filtered);

                // Popular tools from other categories (cross-link)
                const all = await fetchTools();
                const popularSlugs = [
                    "ai-blog-writer",
                    "story-generator",
                    "cover-letter-generator",
                    "ai-essay-writer",
                    "ai-image-generator",
                    "chatgpt-alternative",
                    "math-equation-solver",
                    "rap-lyrics-generator",
                ];
                const pop = all
                    .filter(
                        (t) =>
                            popularSlugs.includes(t.slug) &&
                            t.slug !== currentSlug &&
                            t.category !== category
                    )
                    .slice(0, 4);
                if (!cancelled) setPopular(pop);
            } catch {
                /* ignore */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [currentSlug, category]);

    const Card = ({ t }) => {
        const color = CATEGORY_COLORS[t.category] || "primary";
        const style = getCategoryStyle(color);
        return (
            <Link
                to={`/tool/${t.slug}`}
                data-testid={`related-tool-${t.slug}`}
                className="group nb-border rounded-2xl bg-white p-4 nb-shadow-sm hover:-translate-y-1 hover:shadow-[6px_6px_0px_#0A0A0A] transition-all flex flex-col gap-2 h-full"
            >
                <div className="flex items-start justify-between gap-2">
                    <span
                        className={`${style.bg} nb-border text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full`}
                    >
                        {t.category.replace(/-/g, " ")}
                    </span>
                    <ArrowUpRight strokeWidth={2.5} className="w-4 h-4 shrink-0" />
                </div>
                <div className="font-display font-extrabold text-base leading-tight">
                    {t.name}
                </div>
                <div className="text-xs text-neutral-700 line-clamp-2">
                    {t.description}
                </div>
            </Link>
        );
    };

    if (related.length === 0 && popular.length === 0) return null;

    return (
        <section
            data-testid="related-tools"
            className="mt-12 nb-border-4 nb-shadow rounded-3xl bg-[#FDE047] p-6 md:p-10"
        >
            {related.length > 0 && (
                <div>
                    <div className="text-xs font-bold uppercase tracking-widest text-black/70">
                        Related tools
                    </div>
                    <h2 className="font-display text-3xl md:text-4xl font-black tracking-tighter mt-1">
                        You might also like
                    </h2>
                    <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {related.map((t) => (
                            <Card key={t.slug} t={t} />
                        ))}
                    </div>
                </div>
            )}

            {popular.length > 0 && (
                <div className={related.length > 0 ? "mt-10" : ""}>
                    <div className="text-xs font-bold uppercase tracking-widest text-black/70">
                        Trending across the site
                    </div>
                    <h2 className="font-display text-2xl md:text-3xl font-black tracking-tighter mt-1">
                        Popular Free AI Tools
                    </h2>
                    <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-4">
                        {popular.map((t) => (
                            <Card key={t.slug} t={t} />
                        ))}
                    </div>
                </div>
            )}

            <div className="mt-8">
                <Link
                    to="/#tools"
                    data-testid="see-all-tools"
                    className="inline-flex items-center gap-2 bg-black text-white nb-border rounded-xl px-5 py-3 font-bold text-sm uppercase tracking-widest nb-shadow-sm hover:-translate-y-[2px] hover:shadow-[4px_4px_0px_#0A0A0A] active:translate-y-[2px] active:translate-x-[2px] active:shadow-none transition-all"
                >
                    See all 101 free AI tools
                    <ArrowUpRight strokeWidth={3} className="w-4 h-4" />
                </Link>
            </div>
        </section>
    );
}
